/**
 * Web crypto provider — browser.
 *
 * Uses Web Crypto API for all cryptographic operations and IndexedDB for key
 * persistence.
 *
 * Storage contract (shared with crypto.native.ts — see crypto.d.ts):
 *   - A read returns `null` ONLY when the record is genuinely absent.
 *   - `StorageUnavailableError` means IndexedDB refused, a transaction failed,
 *     or a stored record is unreadable.
 *   - `CryptoUnavailableError` means WebCrypto is missing — an insecure origin.
 *
 * Nothing here maps a failure to `null`. Three paths used to, and every one of
 * them ended at the recovery-phrase screen: the root layout routes `null` there,
 * so a swallowed error told users to type 24 words for a key that was still in
 * this browser.
 */

import { CryptoUnavailableError, StorageUnavailableError } from "./cryptoErrors";

const KEY_DB_NAME = "cynosure_e2ee";
const KEY_STORE_NAME = "keys";

// No unscoped fallback. `userId` is required so a null `currentUserId` cannot
// quietly address a global record that no writer uses.
function recordId(userId: string): string {
  return `encryption_key_${userId}`;
}

function seedRecordId(userId: string): string {
  return `x25519_seed_${userId}`;
}

/**
 * `crypto.subtle` is undefined on insecure origins in every browser — and a
 * LAN dev server over plain http is exactly that (Dockerfile.web runs
 * `--host lan`). `crypto.getRandomValues` is NOT gated, so phrase generation
 * works and the failure appears only here, one step later.
 */
function subtle(): SubtleCrypto {
  const s = globalThis.crypto?.subtle;
  if (!s) {
    throw new CryptoUnavailableError(
      "This page must be opened over HTTPS to use encryption. The browser withholds Web Crypto on insecure addresses."
    );
  }
  return s;
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object" && "message" in err) return String(err.message);
  return String(err);
}

export function getRandomBytes(size: number): Uint8Array {
  const bytes = new Uint8Array(size);
  crypto.getRandomValues(bytes);
  return bytes;
}

export async function deriveKeyRaw(
  phrase: Uint8Array,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> {
  const keyMaterial = await subtle().importKey(
    "raw",
    phrase as Uint8Array<ArrayBuffer>,
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return subtle().deriveKey(
    {
      name: "PBKDF2",
      salt: salt as Uint8Array<ArrayBuffer>,
      iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const hash = await subtle().digest("SHA-256", data as Uint8Array<ArrayBuffer>);
  return new Uint8Array(hash);
}

async function toCryptoKey(key: unknown): Promise<CryptoKey> {
  if (key instanceof CryptoKey) return key;
  return subtle().importKey(
    "raw",
    key as Uint8Array<ArrayBuffer>,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function aesGcmEncrypt(
  key: unknown,
  iv: Uint8Array,
  plaintext: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await toCryptoKey(key);
  const ciphertext = await subtle().encrypt(
    { name: "AES-GCM", iv: iv as Uint8Array<ArrayBuffer> },
    cryptoKey,
    plaintext as Uint8Array<ArrayBuffer>
  );
  return new Uint8Array(ciphertext);
}

export async function aesGcmDecrypt(
  key: unknown,
  iv: Uint8Array,
  ciphertext: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await toCryptoKey(key);
  const decrypted = await subtle().decrypt(
    { name: "AES-GCM", iv: iv as Uint8Array<ArrayBuffer> },
    cryptoKey,
    ciphertext as Uint8Array<ArrayBuffer>
  );
  return new Uint8Array(decrypted);
}

// ── IndexedDB ───────────────────────────────────────────────────────────────

interface KeyRecord {
  id: string;
  /** Device key, as a JWK. */
  jwk?: JsonWebKey;
  /** Seed, as plain bytes — structured-clone-safe on every engine. */
  raw?: number[];
}

function openKeyDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(
        new StorageUnavailableError(
          "This browser does not provide IndexedDB, so the encryption key cannot be kept here."
        )
      );
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(KEY_DB_NAME, 1);
    } catch (err) {
      // Safari Private Browsing and "Block All Cookies" throw here rather than
      // failing the request asynchronously.
      reject(
        new StorageUnavailableError(
          `This browser is blocking site storage, so the encryption key cannot be kept here. Private Browsing and blocked cookies both cause this. (${describe(err)})`
        )
      );
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(KEY_STORE_NAME)) {
        db.createObjectStore(KEY_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new StorageUnavailableError(
          `Could not open the browser's key storage: ${describe(request.error)}`
        )
      );
    // Without this the promise would simply never settle if a version change
    // were ever blocked by another open tab. The version is pinned at 1 today,
    // so it is latent — but a hang is the worst possible failure mode here.
    request.onblocked = () =>
      reject(
        new StorageUnavailableError(
          "The browser's key storage is held open by another Cynosure tab. Close the other tab and try again."
        )
      );
  });
}

async function readRecord(id: string): Promise<KeyRecord | null> {
  const db = await openKeyDB();
  try {
    return await new Promise<KeyRecord | null>((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, "readonly");
      const request = tx.objectStore(KEY_STORE_NAME).get(id);
      request.onsuccess = () => resolve((request.result as KeyRecord | undefined) ?? null);
      request.onerror = () =>
        reject(
          new StorageUnavailableError(
            `Could not read the browser's key storage: ${describe(request.error)}`
          )
        );
    });
  } finally {
    db.close();
  }
}

async function writeRecord(record: KeyRecord): Promise<void> {
  const db = await openKeyDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, "readwrite");
      tx.objectStore(KEY_STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(
          new StorageUnavailableError(
            `Could not write to the browser's key storage: ${describe(tx.error)}`
          )
        );
      tx.onabort = () =>
        reject(
          new StorageUnavailableError(
            `The browser aborted the key storage write: ${describe(tx.error)}`
          )
        );
    });
  } finally {
    db.close();
  }
}

async function deleteRecord(id: string): Promise<void> {
  const db = await openKeyDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, "readwrite");
      tx.objectStore(KEY_STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(
          new StorageUnavailableError(
            `Could not delete from the browser's key storage: ${describe(tx.error)}`
          )
        );
    });
  } finally {
    db.close();
  }
}

// ── Device Key Storage ──────────────────────────────────────────────────────
//
// This slot holds the PERSONAL TEAM KEY. Never the PBKDF2 output — that is the
// seed, below. See docs/encryption.md, "User-Scoped Key Naming".

export async function storeKeyToStorage(key: unknown, userId: string): Promise<void> {
  const jwk = await subtle().exportKey("jwk", key as CryptoKey);
  await writeRecord({ id: recordId(userId), jwk });
}

export async function getKeyFromStorage(userId: string): Promise<unknown | null> {
  const record = await readRecord(recordId(userId));
  if (!record?.jwk) return null;

  // Imported AFTER the transaction has settled. An `await` inside an IDB
  // request handler auto-commits the transaction, which is fine until someone
  // adds a second request after it — so the import lives out here on principle.
  try {
    return await subtle().importKey(
      "jwk",
      record.jwk,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  } catch (err) {
    if (err instanceof CryptoUnavailableError) throw err;
    // A present-but-unreadable key is not "no key". Report it as what it is,
    // so the user is told the stored key is corrupt instead of being handed a
    // blank recovery form with no explanation.
    throw new StorageUnavailableError(
      `The encryption key stored in this browser is unreadable: ${describe(err)}`
    );
  }
}

export async function clearKeyFromStorage(userId: string): Promise<void> {
  await deleteRecord(recordId(userId));
}

export async function exportKeyAsRawBytes(userId: string): Promise<Uint8Array> {
  const key = await getKeyFromStorage(userId);
  if (!key) throw new Error("No encryption key in storage");
  const raw = await subtle().exportKey("raw", key as CryptoKey);
  return new Uint8Array(raw);
}

export async function importAndStoreRawKey(rawBytes: Uint8Array, userId: string): Promise<void> {
  const key = await subtle().importKey(
    "raw",
    rawBytes as Uint8Array<ArrayBuffer>,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  await storeKeyToStorage(key, userId);
}

export async function cryptoKeyToRawBytes(key: unknown): Promise<Uint8Array> {
  const raw = await subtle().exportKey("raw", key as CryptoKey);
  return new Uint8Array(raw);
}

// ── PBKDF2 Seed Storage (for QR transfer + X25519 derivation) ──────────────

export async function storeSeed(seed: Uint8Array, userId: string): Promise<void> {
  await writeRecord({ id: seedRecordId(userId), raw: Array.from(seed) });
}

export async function getSeed(userId: string): Promise<Uint8Array | null> {
  const record = await readRecord(seedRecordId(userId));
  if (!record?.raw) return null;
  return new Uint8Array(record.raw);
}

export async function clearSeed(userId: string): Promise<void> {
  await deleteRecord(seedRecordId(userId));
}
